import { formatAmount } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@loot-os/ui/components/chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useParams, useRouter, useSearch } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useMemo } from "react";
import { Bar, ComposedChart, Line, XAxis, YAxis } from "recharts";
import { PurchaseStatusBadge } from "../components/PurchaseStatusBadge";
import { amountColumnHeader, useCurrency } from "../lib/currency";
import { centsToDollars, formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";

type Granularity = "day" | "month" | "year";

const DAY_MS = 24 * 60 * 60 * 1000;
/** Trailing report window per granularity; year is unbounded. */
const WINDOW = { day: 90, month: 12 } as const;

const revenueChartConfig = {
  revenue: { label: "Revenue", color: "var(--chart-1)" },
  profit: { label: "Profit", color: "var(--chart-2)" },
} satisfies ChartConfig;

/** Start of the default window, UTC. undefined = all time (year view). */
function windowStart(g: Granularity, now: Date): Date | undefined {
  if (g === "day") {
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return new Date(today - (WINDOW.day - 1) * DAY_MS);
  }
  if (g === "month") {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (WINDOW.month - 1), 1));
  }
  return undefined;
}

/** Every bucket key from `from` through `now`, so the chart has no holes. */
function enumerateBuckets(g: Granularity, from: Date, now: Date): string[] {
  const keys: string[] = [];
  if (g === "day") {
    const end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    for (let t = from.getTime(); t <= end; t += DAY_MS) {
      keys.push(new Date(t).toISOString().slice(0, 10));
    }
  } else if (g === "month") {
    let y = from.getUTCFullYear();
    let m = from.getUTCMonth();
    while (y < now.getUTCFullYear() || (y === now.getUTCFullYear() && m <= now.getUTCMonth())) {
      keys.push(`${y}-${String(m + 1).padStart(2, "0")}`);
      m += 1;
      if (m === 12) {
        m = 0;
        y += 1;
      }
    }
  } else {
    for (let y = from.getUTCFullYear(); y <= now.getUTCFullYear(); y += 1) {
      keys.push(String(y));
    }
  }
  return keys;
}

/** Half-open [from, to) UTC span of one bucket, for filtering the invoice list. */
function bucketToRange(g: Granularity, bucket: string): { from: Date; to: Date } {
  if (g === "day") {
    const from = new Date(`${bucket}T00:00:00Z`);
    return { from, to: new Date(from.getTime() + DAY_MS) };
  }
  if (g === "month") {
    const [y = 0, m = 1] = bucket.split("-").map(Number);
    return { from: new Date(Date.UTC(y, m - 1, 1)), to: new Date(Date.UTC(y, m, 1)) };
  }
  const y = Number(bucket);
  return { from: new Date(Date.UTC(y, 0, 1)), to: new Date(Date.UTC(y + 1, 0, 1)) };
}

/** "2026-08-21" → "Aug 21, 2026" · "2026-08" → "Aug 2026" · "2026" → "2026". Pinned UTC. */
function bucketLabel(g: Granularity, bucket: string): string {
  const { from } = bucketToRange(g, bucket);
  if (g === "day") {
    return from.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  }
  if (g === "month") {
    return from.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
  }
  return bucket;
}

/** Compact x-axis tick: days as "Aug 21", months as "Aug 26", years as-is. */
function tickLabel(g: Granularity, bucket: string): string {
  const { from } = bucketToRange(g, bucket);
  if (g === "day") {
    return from.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  }
  if (g === "month") {
    return from.toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
  }
  return bucket;
}

/** The dense day view labels only its first and last tick; month/year label all. */
function dayEdgeTicks(data: { bucket: string }[]): string[] | undefined {
  const first = data.at(0);
  const last = data.at(-1);
  if (!first || !last || first === last) return undefined;
  return [first.bucket, last.bucket];
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="gap-1 py-4">
      <CardHeader className="px-4">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent className="px-4 text-2xl font-semibold tabular-nums">{value}</CardContent>
    </Card>
  );
}

export function RevenuePage() {
  const trpc = useTRPC();
  const currency = useCurrency();
  const router = useRouter();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  // The environment rides the x-loot-environment header from the tenant switcher.
  const search = useSearch({ strict: false }) as { g?: Granularity; bucket?: string };
  const g = search.g ?? "month";
  const selectedBucket = search.bucket;

  const setSearch = (next: { g?: Granularity; bucket?: string }) => {
    void router.navigate({
      to: "/tenants/$tenantId/storefront/payments/revenue",
      params: { tenantId },
      search: next,
      replace: true,
    });
  };

  // Stable across renders so the query input doesn't churn every render.
  const from = useMemo(() => windowStart(g, new Date()), [g]);

  const revenue = useQuery(
    trpc.payments.revenue.queryOptions(
      { granularity: g, from },
      { placeholderData: (previous) => previous },
    ),
  );

  const bucketRange = selectedBucket ? bucketToRange(g, selectedBucket) : {};
  const purchases = useInfiniteQuery(
    trpc.payments.purchases.infiniteQueryOptions(
      { limit: 50, ...bucketRange },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const invoices = purchases.data?.pages.flatMap((page) => page.data) ?? [];

  // Everything is USD today; the aggregation still groups by currency so a
  // future non-USD row surfaces here instead of summing silently.
  const buckets = revenue.data ?? [];
  const hasForeignCurrency = buckets.some((b) => b.currency !== "usd");
  const totals = buckets.reduce(
    (acc, b) => ({
      grossCents: acc.grossCents + b.grossCents,
      refundedCents: acc.refundedCents + b.refundedCents,
      disputedCents: acc.disputedCents + b.disputedCents,
      netCents: acc.netCents + b.netCents,
      count: acc.count + b.count,
    }),
    { grossCents: 0, refundedCents: 0, disputedCents: 0, netCents: 0, count: 0 },
  );

  // Chronological + zero-filled so the chart never draws a gap as a cliff. The
  // aggregation returns newest-first with only non-empty buckets.
  const chartData = useMemo(() => {
    const byBucket = new Map<string, { grossCents: number; netCents: number }>();
    for (const b of buckets) {
      const prev = byBucket.get(b.bucket) ?? { grossCents: 0, netCents: 0 };
      byBucket.set(b.bucket, {
        grossCents: prev.grossCents + b.grossCents,
        netCents: prev.netCents + b.netCents,
      });
    }
    const oldest = buckets.at(-1)?.bucket;
    const start =
      from ?? (oldest ? bucketToRange(g, oldest).from : windowStart("month", new Date()));
    return enumerateBuckets(g, start ?? new Date(), new Date()).map((bucket) => ({
      bucket,
      revenue: (byBucket.get(bucket)?.grossCents ?? 0) / 100,
      profit: (byBucket.get(bucket)?.netCents ?? 0) / 100,
    }));
  }, [buckets, from, g]);

  const isStale = revenue.isPlaceholderData;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Revenue</h1>
        <p className="text-sm text-muted-foreground">
          Top-up revenue in this environment, bucketed by UTC period of payment completion. Profit
          is revenue net of refunds and disputes — Stripe processing fees are not included. Payouts
          to the merchant are handled manually outside Stripe; these totals are the basis.
        </p>
      </div>

      <Tabs value={g} onValueChange={(value) => setSearch({ g: value as Granularity })}>
        <TabsList>
          <TabsTrigger value="day">Day</TabsTrigger>
          <TabsTrigger value="month">Month</TabsTrigger>
          <TabsTrigger value="year">Year</TabsTrigger>
        </TabsList>
      </Tabs>

      {hasForeignCurrency && (
        <p className="text-xs text-amber-700 dark:text-amber-500">
          Non-USD purchases exist in this window — the sums below mix currencies.
        </p>
      )}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
        <StatCard label="Gross" value={revenue.data ? centsToDollars(totals.grossCents) : "—"} />
        <StatCard
          label="Refunded"
          value={revenue.data ? centsToDollars(totals.refundedCents) : "—"}
        />
        <StatCard
          label="Disputed"
          value={revenue.data ? centsToDollars(totals.disputedCents) : "—"}
        />
        <StatCard
          label="Net revenue"
          value={revenue.data ? centsToDollars(totals.netCents) : "—"}
        />
        <StatCard label="Purchases" value={revenue.data ? formatNumber(totals.count) : "—"} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue & profit over time</CardTitle>
          <CardDescription>
            {g === "day" && `Daily, trailing ${WINDOW.day} days.`}
            {g === "month" && `Monthly, trailing ${WINDOW.month} months.`}
            {g === "year" && "Yearly, all time."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer config={revenueChartConfig} className="h-64 w-full">
            <ComposedChart data={chartData} margin={{ left: 0, right: 0, top: 4 }}>
              <XAxis
                dataKey="bucket"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                ticks={g === "day" ? dayEdgeTicks(chartData) : undefined}
                tickFormatter={(value: string) => tickLabel(g, value)}
                className="text-[10px]"
              />
              <YAxis hide domain={[0, "dataMax"]} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(label) =>
                      typeof label === "string" ? bucketLabel(g, label) : label
                    }
                    formatter={(value, name) => (
                      <>
                        <div
                          className="h-2 w-2 shrink-0 rounded-[2px]"
                          style={{ backgroundColor: `var(--color-${name})` }}
                        />
                        {revenueChartConfig[name as keyof typeof revenueChartConfig]?.label ?? name}
                        <div className="ml-auto pl-4 font-mono font-medium tabular-nums text-foreground">
                          {`$${Number(value).toFixed(2)}`}
                        </div>
                      </>
                    )}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar
                dataKey="revenue"
                fill="var(--color-revenue)"
                radius={[4, 4, 0, 0]}
                maxBarSize={16}
              />
              <Line
                dataKey="profit"
                type="monotone"
                stroke="var(--color-profit)"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Periods</CardTitle>
          <CardDescription>
            Click a period to filter the invoice list below. Refunds and disputes stay in the period
            the payment completed, as clawbacks against its gross.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table aria-busy={isStale} className={isStale ? "opacity-60 transition-opacity" : ""}>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Purchases</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Refunded</TableHead>
                <TableHead className="text-right">Disputed</TableHead>
                <TableHead className="text-right">Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {revenue.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {revenue.isSuccess && buckets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    No completed purchases in this window.
                  </TableCell>
                </TableRow>
              )}
              {buckets.map((b) => (
                <TableRow
                  key={`${b.bucket}-${b.currency}`}
                  className="cursor-pointer"
                  data-state={selectedBucket === b.bucket ? "selected" : undefined}
                  onClick={() =>
                    setSearch({ g, bucket: selectedBucket === b.bucket ? undefined : b.bucket })
                  }
                >
                  <TableCell>
                    {bucketLabel(g, b.bucket)}
                    {b.currency !== "usd" && (
                      <span className="ml-2 font-mono text-xs text-amber-700 dark:text-amber-500">
                        {b.currency}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatNumber(b.count)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {centsToDollars(b.grossCents)}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono text-xs ${b.refundedCents > 0 ? "text-destructive" : "text-muted-foreground"}`}
                  >
                    {centsToDollars(b.refundedCents)}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono text-xs ${b.disputedCents > 0 ? "text-destructive" : "text-muted-foreground"}`}
                  >
                    {centsToDollars(b.disputedCents)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs font-medium">
                    {centsToDollars(b.netCents)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {selectedBucket ? `Invoices — ${bucketLabel(g, selectedBucket)}` : "All invoices"}
          </CardTitle>
          <CardDescription>
            {selectedBucket
              ? "Purchases that completed in this period."
              : "Every balance purchase in this environment, newest first."}
          </CardDescription>
          {selectedBucket && (
            <CardAction>
              <Button variant="ghost" size="sm" onClick={() => setSearch({ g })}>
                <X />
                Clear filter
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Purchase</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">
                  {amountColumnHeader("Credited", currency)}
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Stripe refs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchases.isLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {purchases.isSuccess && invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    {selectedBucket ? "No purchases in this period." : "No purchases yet."}
                  </TableCell>
                </TableRow>
              )}
              {invoices.map((purchase) => (
                <TableRow key={purchase.id}>
                  <TableCell className="text-muted-foreground">
                    {formatDate(purchase.createdAt)}
                  </TableCell>
                  <TableCell>
                    {purchase.shopperEmail ? (
                      <Link
                        to="/tenants/$tenantId/users"
                        params={{ tenantId }}
                        search={{ user: purchase.endUserId }}
                        className="underline underline-offset-2 hover:text-primary"
                      >
                        {purchase.shopperEmail}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>{purchase.packName || purchase.packId}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {centsToDollars(purchase.amountCents)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatAmount(purchase.creditedCents, currency)}
                  </TableCell>
                  <TableCell>
                    <PurchaseStatusBadge status={purchase.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {purchase.completedAt ? formatDate(purchase.completedAt) : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5 font-mono text-xs text-muted-foreground">
                      <span title="Checkout session">{purchase.checkoutSessionId}</span>
                      {purchase.paymentIntentId && (
                        <span title="Payment intent">{purchase.paymentIntentId}</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {purchases.hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              className="self-center"
              disabled={purchases.isFetchingNextPage}
              onClick={() => void purchases.fetchNextPage()}
            >
              {purchases.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
